// src/api/auth.js
// Canon: driver login goes through Backend: POST /api/drivers/login
// Note: V2 is used for orders (/api/v2), BUT driver login is legacy-canon: /api/drivers/login
//
// You can set:
// - VITE_API_BASE  (e.g. /api/v2 or https://backend.../api/v2) for v2 endpoints
// - VITE_AUTH_BASE (e.g. /api or https://backend.../api) for auth endpoints
//
// If VITE_AUTH_BASE is not provided, we derive it from VITE_API_BASE by stripping "/v2".

const API_BASE = import.meta.env.VITE_API_BASE || "/api";

const AUTH_BASE = (() => {
  const explicit = import.meta.env.VITE_AUTH_BASE;
  if (explicit) return explicit;

  const base = API_BASE || "/api";
  const norm = base.replace(/\/+$/, ""); // trim trailing "/"

  // If base ends with "/api/v2" -> "/api"
  if (/\/api\/v2$/i.test(norm)) return norm.replace(/\/api\/v2$/i, "/api");

  // If base ends with "/v2" -> strip it
  if (/\/v2$/i.test(norm)) return norm.replace(/\/v2$/i, "");

  // Otherwise assume base is already correct for auth
  return norm || "/api";
})();

async function apiPost(base, path, payload) {
  const res = await fetch(base + path, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
    },
    body: JSON.stringify(payload || {}),
    credentials: "include",
  });

  const text = await res.text().catch(() => "");
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {};
  }

  if (!res.ok) {
    const msg = data?.error || data?.message || ("HTTP " + res.status);
    const err = new Error(msg);
    err.code = String(data?.code || data?.error || msg || "").trim();
    err.status = res.status;
    err.data = data;
    throw err;
  }

  const ctype = String(res.headers.get("content-type") || "").toLowerCase();
  const looksLikeHtml = /<!doctype html|<html[\s>]/i.test(text);

  if (!ctype.includes("application/json") && looksLikeHtml) {
    throw new Error("BAD_API_BASE");
  }

  return data;
}

export async function loginDriver(loginOrPayload, password) {
  const payload =
    loginOrPayload && typeof loginOrPayload === "object"
      ? loginOrPayload
      : { login: loginOrPayload, password };

  // IMPORTANT: login endpoint is /api/drivers/login (NOT /api/v2/...)
  return apiPost(AUTH_BASE, "/drivers/login", payload);
}

export async function registerDriver(payload = {}) {
  // IMPORTANT: register endpoint is /api/drivers/register (NOT /api/v2/...)
  return apiPost(AUTH_BASE, "/drivers/register", payload || {});
}


export async function uploadDriverPublicVehiclePhoto(driverId, token, file) {
  const id = String(driverId || "").trim();
  if (!id) throw new Error("DRIVER_ID_MISSING");
  if (!file) throw new Error("VEHICLE_PHOTO_REQUIRED");

  const formData = new FormData();
  formData.append("file", file, file?.name || "vehicle.jpg");

  const headers = {};
  const authToken = String(token || "").trim();
  if (authToken) {
    headers.Authorization = /^bearer\s+/i.test(authToken)
      ? authToken
      : `Bearer ${authToken}`;
  }

  const res = await fetch(
    `${AUTH_BASE}/drivers/${encodeURIComponent(id)}/public-vehicle-photo`,
    {
      method: "POST",
      headers,
      body: formData,
      credentials: "include",
    }
  );

  const text = await res.text().catch(() => "");
  let data = {};
  try {
    data = text ? JSON.parse(text) : {};
  } catch {
    data = {};
  }

  if (!res.ok || data?.ok === false) {
    const msg =
      data?.error ||
      data?.message ||
      `HTTP_${res.status}`;
    const err = new Error(msg);
    err.code = String(data?.error || msg || "").trim();
    err.status = res.status;
    err.data = data;
    throw err;
  }

  return data;
}
